import Accordion from '@/common/components/Accordion';
import { formatCurrency, formatPercentage } from '@/common/utils/formatters';
import { ChevronDownCircleIcon, KeyboardIcon, SquareCheckIcon, UserIcon } from 'lucide-react';
import { EstablishmentCompanyData } from '@/common/types/establishment/company.type';
import Box from '@/common/components/Box';
import { UpdateFinancialModal } from '../../Update/components/UpdateFinancialPoliticsModal';
import Spinner from '@/common/components/Spinner';
import { useIsFetching, useIsMutating } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';

const FinancialSection = ({ establishmentCompany }: { establishmentCompany?: EstablishmentCompanyData }) => {
  const { id } = useParams();

  const [displayFetching, setDisplayFetching] = useState(false);

  const isMutating = useIsMutating();
  const isFetching = useIsFetching({ queryKey: ['establishmentCompany', id] })

  useEffect(() => {
    isMutating && setDisplayFetching(true)
  }, [isMutating]);

  useEffect(() => {
    !isFetching && setDisplayFetching(false)
  }, [isFetching]);

  return (
    <Accordion
      defaultOpen
      head={(
        <div className="flex items-center gap-x-3">
          <div onClick={(e) => e.stopPropagation()}>
            <UpdateFinancialModal establishmentCompany={establishmentCompany} isFetching={displayFetching && !!isFetching} />
          </div>

          <p className='text-xl'>
            Financeiro
          </p>

          {displayFetching && !!isFetching && <Spinner className='fill-white-400 size-4' />}
        </div>
      )}
      triggerClassName="py-0"
      bodyClassName="pt-4 pb-0"
      body={(
        <div className='flex flex-col gap-4'>
          <div className='flex items-center gap-3'>
            <p>
              Taxa diária de juros de adiantamento:
            </p>

            <div className='flex items-center gap-2 text-white-400'>
              <KeyboardIcon strokeWidth={1.5} />

              <div>
                <p className='italic font-light text-sm'>
                  Juros
                </p>

                <p className='text-white-800'>
                  {establishmentCompany?.matrix_branch_payment_advance_daily_interest
                    ? formatPercentage(Number(establishmentCompany?.matrix_branch_payment_advance_daily_interest))
                    : '--'}
                </p>
              </div>
            </div>
          </div>

          <div className='flex items-center gap-3 flex-wrap'>
            <p>
              Meios de captura:
            </p>

            {Number(establishmentCompany?.matrix_branch?.capture_methods?.length) > 0 ? establishmentCompany?.matrix_branch?.capture_methods?.map(({ capture_method }, i) => (
              <div key={`product_category_${i}`} className='flex items-center gap-1 text-white-400'>
                <SquareCheckIcon strokeWidth={1.5} />

                <p className='text-white-800'>
                  {capture_method.display_name}
                </p>
              </div>
            )) : (
              <p className='italic font-light'>
                Não possui
              </p>
            )}
          </div>

          <div className='flex items-center gap-3'>
            <p>
              Dados bancários:
            </p>

            <Box className='flex items-center gap-4 p-4'>
              <div className='flex items-center gap-2'>
                <ChevronDownCircleIcon strokeWidth={1.5} className='text-white-400' />

                <div>
                  <p className='italic text-xs font-light'>
                    Banco
                  </p>

                  <p className='text-white-800'>
                    {establishmentCompany?.matrix_branch?.bank_account_information?.bank_institution.name || '--'}
                  </p>
                </div>
              </div>

              <div className='flex items-center gap-2'>
                <KeyboardIcon strokeWidth={1.5} className='text-white-400' />

                <div>
                  <p className='italic text-xs font-light'>
                    Agência
                  </p>

                  <p className='text-white-800'>
                    {establishmentCompany?.matrix_branch?.bank_account_information?.agency || '--'}
                  </p>
                </div>
              </div>

              <div className='flex items-center gap-2'>
                <KeyboardIcon strokeWidth={1.5} className='text-white-400' />

                <div>
                  <p className='italic text-xs font-light'>
                    Conta
                  </p>

                  <p className='text-white-800'>
                    {establishmentCompany?.matrix_branch?.bank_account_information?.account || '--'}
                  </p>
                </div>
              </div>

              <div className='flex items-center gap-2'>
                <UserIcon strokeWidth={1.5} className='text-white-400' />

                <div>
                  <p className='italic text-xs font-light'>
                    Favorecido
                  </p>

                  <p className='text-white-800'>
                    {establishmentCompany?.matrix_branch?.bank_account_information?.payee_name || '--'}
                  </p>
                </div>
              </div>
            </Box>
          </div>

          <div className='flex items-center gap-3'>
            <p>
              Valor mínimo para adiantamento:
            </p>

            <div className='flex items-center gap-2 text-white-400'>
              <KeyboardIcon strokeWidth={1.5} />

              <div>
                <p className='italic font-light text-sm'>
                  Valor mínimo
                </p>

                <p className='text-white-800'>
                  {establishmentCompany?.matrix_branch_payment_advance_min_total_to_be_paid
                    ? formatCurrency(Number(establishmentCompany?.matrix_branch_payment_advance_min_total_to_be_paid))
                    : '--'}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    />
  )
}

export default FinancialSection;
